import NewFileUploader from '@components/NewFileUploader';
import { Editor } from '@components/shared/HtmlEditor/HtmlEditor';
import { OfficeViewer } from '@components/shared/OfficeViewer';
import { PdfViewer } from '@components/shared/PdfViewer';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import { Button } from '@mui/material';
import { isOfficeFile, isPdfFile } from '@utils/fileUtils';
import { hasAdminSecondaryRole } from 'services/auth/authUtils';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadStore } from 'services/file-manager/file-managerStore';
import { useTopicAttachments } from 'services/monitoring-offers/monitoringOffersHooks';
import { createTopicFiles } from 'services/monitoring-offers/monitoringOffersService';
import { useUsersStore } from 'services/users/usersStore';
import Lightbox from 'yet-another-react-lightbox';
import './submitModal.css';

interface SubmitModalProps {
	onClose: () => void;
	onSuccess?: () => void;
}

const isImageFile = (fileName: string) => {
	const extension = fileName.split('.').pop()?.toLowerCase();
	return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(extension || '');
};

const getFileIcon = (fileName: string) => {
	const extension = fileName.split('.').pop()?.toLowerCase();

	if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) {
		return <ImageIcon color="primary" />;
	} else if (extension === 'pdf') {
		return <PictureAsPdfIcon color="error" />;
	} else if (['doc', 'docx'].includes(extension || '')) {
		return <DescriptionIcon color="info" />;
	} else if (['xlsx', 'xls'].includes(extension || '')) {
		return <TableViewIcon color="success" />;
	} else {
		return <InsertDriveFileIcon color="action" />;
	}
};

export default function SubmitModal({ onClose, onSuccess }: SubmitModalProps) {
	const { t } = useTranslation();
	const { user } = useUsersStore();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const globalLoading = useUploadStore((state) => state.globalLoading);

	const [uploadedFileKeys, setUploadedFileKeys] = useState<any>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [fileUploading, setFileUploading] = useState(false);
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);

	// PDF Viewer state
	const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
	const [currentPdfUrl, setCurrentPdfUrl] = useState('');
	const [currentPdfFileName, setCurrentPdfFileName] = useState('');

	// Office Viewer state
	const [officeViewerOpen, setOfficeViewerOpen] = useState(false);
	const [currentOfficeUrl, setCurrentOfficeUrl] = useState('');
	const [currentOfficeFileName, setCurrentOfficeFileName] = useState('');

	const topicId = localStorage.getItem('topic_id');
	const categoryId = localStorage.getItem('category_id');
	const phaseId = localStorage.getItem('phase_id');
	const selectedOffer = localStorage.getItem('selectedOffer');

	const offer = JSON.parse(selectedOffer || '{}');
	const isCompleted = moment(offer?.phases_management?.active[0]?.phases_closed).isBefore(moment());
	
	// Ensure we have the correct monitoring ID, which is crucial for API calls
	const requestId = offer.id || offer.monitoring_id;
	
	const isFarmerPath = user?.role?.toLowerCase() === 'farmer';
	
	// Check if the user has admin secondary role
	const isAdmin = hasAdminSecondaryRole();
	
	// Determine if the user can upload files (either not a farmer or is a farmer with admin rights)
	const canUploadFiles = !isFarmerPath || isAdmin;

	// Log for debugging
	useEffect(() => {
		console.log('Topic ID:', topicId);
		console.log('Category ID:', categoryId);
		console.log('Request ID:', requestId);
	}, [topicId, categoryId, requestId]);

	const {
		data,
		isLoading: attachmentsLoading,
		removeAttachment,
		refreshAttachments,
		error: attachmentsError,
	} = useTopicAttachments(topicId || '', categoryId || '', requestId);

	const [comment, setComment] = useState('');

	// Update comment when data changes
	useEffect(() => {
		if (data?.topic_comment) {
			setComment(data.topic_comment);
		}
	}, [data]);

	const hasFiles = (data?.attachments?.length || 0) > 0 || uploadedFileKeys.length > 0;

	const allowedFormats = [
		'.jpg',
		'.jpeg',
		'.png',
		'.gif',
		'.webp',
		'.pdf',
		'.doc',
		'.docx',
		'.kmz',
		'.kml',
		'.xlsx',
		'.xls',
	];

	const handleSubmit = async () => {
		setIsLoading(true);
		try {
			const formattedFilesKey = uploadedFileKeys.map((file: any) => ({
				file: file.key,
			}));

			const payload = {
				phases_id: phaseId,
				category_id: categoryId,
				request_id: requestId,
				topic_id: topicId,
				files_key: formattedFilesKey,
				topic_comment: comment,
			};
			await createTopicFiles(payload);

			setIsLoading(false);
			setUploadedFileKeys([]);
			setComment('');
			showSnackbar(t('success'), t('topicUploadedSuccess'), 'success');

			if (onSuccess) {
				onSuccess();
			}

			onClose();
		} catch (error) {
			console.error('Error uploading topic files:', error);
			showSnackbar(t('error'), t('topicUploadedError'), 'error');
			setIsLoading(false);
		}
	};

	const handleNewFileUpload = (status: any) => {
		if (!status || !status.key) return;

		const newFile = {
			key: status.key,
			fileName: status.fileName,
			id: status.key,
		};

		setUploadedFileKeys((prevList: any) => {
			const newList = [...prevList, newFile];
			return newList;
		});
	};

	// Monitor global upload state from file-managerStore
	useEffect(() => {
		setFileUploading(globalLoading);
	}, [globalLoading]);

	const handleFileRemove = async (fileId: string) => {
		try {
			const success = await removeAttachment(fileId);

			if (success) {
				await refreshAttachments();
				showSnackbar(t('success'), t('fileRemovedSuccessfully'), 'success');
			}
		} catch (error) {
			console.error('Error removing file:', error);
			showSnackbar(t('error'), t('failedToRemoveFile'), 'error');
		}
	};

	// Handle removing newly uploaded files
	const handleNewFileRemove = useCallback(
		(index: number, file: any) => {
			if (file && file.key) {
				setUploadedFileKeys((prevList: any) =>
					prevList.filter((item: any) => item.key !== file.key)
				);
			}
		},
		[showSnackbar, t]
	);

	const handleFileClick = (fileUrl: string, fileName: string, fileType?: string) => {
		if (isPdfFile(fileName)) {
			setCurrentPdfUrl(fileUrl);
			setCurrentPdfFileName(fileName);
			setPdfViewerOpen(true);
		} else if (isOfficeFile(fileName)) {
			setCurrentOfficeUrl(fileUrl);
			setCurrentOfficeFileName(fileName);
			setOfficeViewerOpen(true);
		} else {
			window.open(fileUrl, '_blank');
		}
	};
	return (
		<div className="w-full flex flex-col">
			{isFarmerPath && !isCompleted && canUploadFiles ? (
				<NewFileUploader
					onExistingFileRemove={handleFileRemove}
					onFileRemove={handleNewFileRemove}
					showRemoveButton={true}
					onUploaded={handleNewFileUpload}
					title={t('attachFiles')}
					description={t('uploadAndAttachFiles')}
					allowedFormats={allowedFormats}
					multiple={true}
					existingFiles={data?.attachments?.map((attachment: any) => ({
						id: attachment.id || '',
						fileType: attachment.fileType || 'document',
						s3_key: attachment.s3_key || '',
						filename: attachment.filename || attachment.file_name || '',
					}))}
				/>
			) : (
				<div>
					<p className="text-[#343A40] font-bold text-base mb-2">{t('files')}</p>
					{data?.attachments?.length > 0 ? (
						<div className="flex flex-col gap-4">
							{data?.attachments?.map((attachment: any, index: number) => (
								<div key={attachment.id} className="flex items-center gap-2 w-full">
									{isImageFile(attachment.filename) ? (
										<img
											src={attachment.s3_key}
											alt={attachment.filename}
											className="w-10 h-10 rounded-md cursor-pointer"
											onClick={(e) => {
												e.stopPropagation();
												setCurrentImageIndex(index);
												setIsLightboxOpen(true);
											}}
										/>
									) : (
										<div
											className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-md cursor-pointer"
											onClick={() => handleFileClick(attachment.s3_key, attachment.filename)}
										>
											{getFileIcon(attachment.filename)}
										</div>
									)}
									<p
										className="cursor-pointer underline"
										onClick={() => {
											if (isImageFile(attachment.filename)) {
												setCurrentImageIndex(index);
												setIsLightboxOpen(true);
											} else {
												handleFileClick(attachment.s3_key, attachment.filename);
											}
										}}
									>
										{attachment.filename}
									</p>
								</div>
							))}
						</div>
					) : (
						<div className="bg-[#F8F9FA] p-1 text-[#7B809A] rounded-md inline-flex items-center justify-start">
							<span className="flex w-auto">{t('noAttachments')}</span>
						</div>
					)}
				</div>
			)}

			{isFarmerPath && !isCompleted && canUploadFiles ? (
				<Editor
					disabled={false}
					value={comment}
					placeholder={`${t('addComment')}`}
					onChange={(value) => setComment(value)}
					onBlur={() => {}}
				/>
			) : (
				<div className="inline-block mt-5">
					<p className="text-[#343A40] font-bold text-base">{t('comment')}</p>
					{comment ? (
						<div className="mt-2 html-content-view" dangerouslySetInnerHTML={{ __html: comment }} />
					) : (
						<p className="bg-[#F8F9FA] p-1 text-[#7B809A] rounded-md mt-2 inline-block">
							{t('noComment')}
						</p>
					)}
				</div>
			)}

			<div className="flex justify-end mt-4 gap-2">
				<Button
					sx={{
						color: '#343A40',
						fontWeight: 'bold',
						textTransform: 'none',
						'&:hover': {
							backgroundColor: 'transparent',
						},
					}}
					onClick={onClose}
				>
					{isFarmerPath && !isCompleted && canUploadFiles ? t('cancel') : t('close')}
				</Button>
				{isFarmerPath && !isCompleted && canUploadFiles && (
					<Button
						sx={{
							background: '#00AB89',
							fontWeight: 'bold',
							color: 'white',
							textTransform: 'none',
							'&:hover': {
								background: '#009B7B',
							},
						}}
						onClick={handleSubmit}
						loading={isLoading || attachmentsLoading || fileUploading}
						disabled={
							isLoading ||
							attachmentsLoading ||
							fileUploading ||
							(!hasFiles && comment.length === 0)
						}
						variant="contained"
					>
						{t('submit')}
					</Button>
				)}
			</div>

			{data?.attachments && data.attachments.length > 0 && (
				<Lightbox
					open={isLightboxOpen}
					close={() => setIsLightboxOpen(false)}
					slides={data?.attachments
						.filter((attachment: any) => isImageFile(attachment.filename))
						.map((attachment: any) => ({
							src: attachment.s3_key,
							alt: attachment.filename,
						}))}
					index={currentImageIndex}
				/>
			)}

			{/* PDF Viewer component for viewing PDFs */}
			<PdfViewer
				open={pdfViewerOpen}
				onClose={() => setPdfViewerOpen(false)}
				pdfUrl={currentPdfUrl}
				fileName={currentPdfFileName}
			/>

			{/* Office Viewer component for viewing Office documents */}
			<OfficeViewer
				open={officeViewerOpen}
				onClose={() => setOfficeViewerOpen(false)}
				documentUrl={currentOfficeUrl}
				fileName={currentOfficeFileName}
			/>
		</div>
	);
}
